import Link from "next/link";
import { useRouter } from "next/navigation";
import type { ComponentProps, ElementType, MouseEvent } from "react";

import { cn } from "@/utils/lib";

type DelegatedLinkProps<T extends ElementType = "div"> = {
  href: string;
  as?: T;
  enabled?: boolean;
  prefetch?: boolean;
} & ComponentProps<T>;

export const DelegatedLink = ({
  children,
  href,
  prefetch = false,
  enabled = true,
  as: As = "div",
  className,
  ...rest
}: DelegatedLinkProps) => {
  const router = useRouter();

  const handleClick = (e: MouseEvent) => {
    if (!enabled) {
      return;
    }

    const ev = e as unknown as MouseEvent;
    const eventTarget = ev.target as HTMLDivElement;
    const button = ev.nativeEvent.button;
    const rightClick = button === 2;

    // If the click is not on the element itself, ignore it
    // This can happen with content using Portal. Because of React's event system,
    // events will bubble up in the component tree, regardless of its DOM position.
    if (!ev.currentTarget.contains(eventTarget)) {
      return;
    }

    if (rightClick) {
      return;
    }

    const closestClickable = eventTarget.closest(
      "button, a, [role='button'], [role='menuitem'], [role='link']",
    );

    // Click is initiated from within a clickable element, we don't handle,
    // but we also make sure that the closest element is our own wrapper
    if (closestClickable && closestClickable !== ev.currentTarget) {
      return;
    }

    const closestDataHref = eventTarget.closest("[data-href]");
    const hasSelection = !!window?.getSelection()?.toString();

    // If no `data-href` found nearby, we don't handle
    // We also skip if there is a text selection
    if (!closestDataHref || hasSelection) {
      return;
    }

    const href = closestDataHref.getAttribute("data-href")!;

    // Ctrl + Click, Cmd + Click, Middle Mouse Click => new tab
    if (ev.ctrlKey || ev.metaKey || button === 1) {
      window.open(`${window.location.origin}${href}`, "_blank");
      return;
    }

    router.push(href);
  };

  // This will prevent the default scroll wheel behaviour on middle click.
  // This is how it works in general for normal link clicks as well.
  const handleMouseDown = (ev: MouseEvent) => {
    const middleClick = ev.nativeEvent.button === 1;
    if (enabled && middleClick) {
      ev.preventDefault();
    }
  };

  const handleMouseEnter = () => {
    if (enabled && prefetch) {
      router.prefetch(href);
    }
  };

  return (
    <As
      onClick={handleClick}
      onAuxClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseDown={handleMouseDown}
      data-href={href}
      role="link"
      className={cn("relative", className)}
      {...rest}
    >
      <Link
        href={href}
        prefetch={prefetch}
        className="absolute inset-0 z-[-1]"
      />
      {children}
    </As>
  );
};
